import React, { useState } from 'react';
import config from './config';

const TechLandscapePage = ({ user, onBack, setPage, onUsageUpdate }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState({ percent: 0, message: '' });
  
  // Form data
  const [formData, setFormData] = useState({
    technology: '',
    aspect: ''
  });
  
  // Results
  const [results, setResults] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.technology.trim()) {
      setError('Please enter a technology area');
      return;
    }

    setLoading(true);
    setError('');
    setStep(2);
    setProgress({ percent: 10, message: 'Starting landscape analysis...' });

    try {
      const token = localStorage.getItem('token');
      
      // Progress updates
      const progressUpdates = [
        { percent: 20, message: 'Searching patent database...' },
        { percent: 40, message: 'Analyzing filing trends...' },
        { percent: 55, message: 'Identifying key players...' },
        { percent: 70, message: 'Mapping technology areas...' },
        { percent: 85, message: 'Generating strategic insights...' },
        { percent: 95, message: 'Preparing report...' }
      ];
      
      let progressIndex = 0;
      const progressInterval = setInterval(() => {
        if (progressIndex < progressUpdates.length) {
          setProgress(progressUpdates[progressIndex]);
          progressIndex++;
        }
      }, 1200);

      const response = await fetch(`${config.API_URL}/api/tech-landscape`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          technology: formData.technology,
          aspect: formData.aspect,
          token: token
        })
      });

      clearInterval(progressInterval);
      
      const data = await response.json();
      
      if (data.success) {
        setResults(data);
        setStep(3);
        if (onUsageUpdate) onUsageUpdate();
      } else {
        setError(data.error || 'Error analyzing landscape');
        setStep(1);
      }
    } catch (err) {
      setError('Error connecting to server. Please try again.');
      setStep(1);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const resetJourney = () => {
    setStep(1);
    setFormData({ technology: '', aspect: '' });
    setResults(null);
    setError('');
  };

  const getMaturityColor = (maturity) => {
    switch (maturity) {
      case 'Emerging': return { bg: '#dcfce7', text: '#166534', icon: '🌱' };
      case 'Growing': return { bg: '#dbeafe', text: '#1e40af', icon: '📈' };
      case 'Mature': return { bg: '#fef3c7', text: '#92400e', icon: '🏛️' };
      case 'Declining': return { bg: '#fef2f2', text: '#dc2626', icon: '📉' };
      default: return { bg: '#f1f5f9', text: '#475569', icon: '📊' };
    }
  };

  // Save report to database
  const saveReport = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Please log in to save reports');
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${config.API_URL}/api/saved-individual-reports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          report_type: 'tech-landscape',
          title: formData?.technology || 'Technology Landscape Report',
          input_data: formData,
          results_data: results,
          token: token
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        alert(data.error || 'Failed to save report');
      }
    } catch (err) {
      alert('Error saving report');
    } finally {
      setSaving(false);
    }
  };

  // Download as HTML
  const downloadHTML = () => {
    const maturityColors = {
      Emerging: { bg: '#dbeafe', border: '#3b82f6', text: '#1e40af', icon: '🌱' },
      Growing: { bg: '#dcfce7', border: '#16a34a', text: '#166534', icon: '📈' },
      Mature: { bg: '#f3e8ff', border: '#9333ea', text: '#6b21a8', icon: '🏛️' },
      Declining: { bg: '#fee2e2', border: '#dc2626', text: '#991b1b', icon: '📉' }
    };
    const stage = results?.maturity_stage || 'Growing';
    const colors = maturityColors[stage] || maturityColors.Growing;

    const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <title>Technology Landscape: ${formData?.technology || 'Report'}</title>
  <style>
    body { font-family: Arial, sans-serif; max-width: 900px; margin: 0 auto; padding: 20px; line-height: 1.6; }
    h1 { color: #1e293b; border-bottom: 3px solid #7c3aed; padding-bottom: 10px; }
    h2 { color: #7c3aed; margin-top: 30px; }
    .maturity-box { background: ${colors.bg}; border-left: 4px solid ${colors.border}; padding: 20px; margin: 20px 0; border-radius: 8px; }
    .maturity-label { font-size: 24px; font-weight: bold; color: ${colors.text}; }
    .maturity-explanation { color: ${colors.text}; margin-top: 10px; }
    .stats-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; margin: 20px 0; }
    .stat-box { background: #f8fafc; padding: 20px; border-radius: 8px; text-align: center; }
    .stat-number { font-size: 28px; font-weight: bold; color: #7c3aed; }
    .stat-label { font-size: 12px; color: #64748b; margin-top: 5px; }
    .summary-box { background: #f8fafc; padding: 20px; border-radius: 8px; margin: 20px 0; }
    .player-card { background: white; border: 1px solid #e2e8f0; padding: 15px; border-radius: 8px; margin: 10px 0; }
    .player-name { font-weight: bold; color: #1e293b; }
    .player-patents { color: #7c3aed; font-weight: bold; }
    .sub-areas { display: flex; flex-wrap: wrap; gap: 10px; margin: 15px 0; }
    .sub-area-tag { background: #f3e8ff; color: #7c3aed; padding: 8px 16px; border-radius: 20px; font-size: 14px; }
    .insights-section { background: #f8fafc; padding: 20px; border-radius: 8px; margin: 15px 0; }
    .insights-section ul { margin: 10px 0; padding-left: 25px; }
    .insights-section li { margin: 10px 0; }
    .two-column { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin: 20px 0; }
    .opportunity-box { background: #dcfce7; padding: 20px; border-radius: 8px; }
    .opportunity-box h3 { color: #166534; margin-top: 0; }
    .risk-box { background: #fef3c7; padding: 20px; border-radius: 8px; }
    .risk-box h3 { color: #92400e; margin-top: 0; }
    .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e2e8f0; color: #64748b; font-size: 12px; }
  </style>
</head>
<body>
  <h1>Technology Landscape Report</h1>
  <p><strong>Technology:</strong> ${formData?.technology || 'Not specified'}</p>
  ${formData?.aspect ? `<p><strong>Focus Area:</strong> ${formData.aspect}</p>` : ''}

  <div class="maturity-box">
    <div class="maturity-label">${colors.icon} ${stage} Technology</div>
    <div class="maturity-explanation">${results?.maturity_explanation || ''}</div>
  </div>

  <div class="stats-grid">
    <div class="stat-box">
      <div class="stat-number">${results?.total_patents?.toLocaleString() || 0}</div>
      <div class="stat-label">Patents Found</div>
    </div>
    <div class="stat-box">
      <div class="stat-number">${results?.unique_assignees?.toLocaleString() || 0}</div>
      <div class="stat-label">Active Companies</div>
    </div>
    <div class="stat-box">
      <div class="stat-number">${results?.trend_direction === 'increasing' ? '↑' : results?.trend_direction === 'decreasing' ? '↓' : '→'} ${results?.trend_percentage ? Math.abs(results.trend_percentage) + '%' : ''}</div>
      <div class="stat-label">Filing Trend</div>
    </div>
  </div>

  <h2>Executive Summary</h2>
  <div class="summary-box">
    <p>${results?.summary || 'No summary available'}</p>
  </div>

  ${results?.top_players && results.top_players.length > 0 ? `
    <h2>Top Players</h2>
    ${results.top_players.map(player => `
      <div class="player-card">
        <span class="player-name">${player.name}</span> - <span class="player-patents">${player.patents} patents</span>
        <p style="margin: 5px 0 0 0; color: #64748b;">${player.focus || ''}</p>
      </div>
    `).join('')}
  ` : ''}

  ${results?.sub_areas && results.sub_areas.length > 0 ? `
    <h2>Key Technology Sub-Areas</h2>
    <div class="sub-areas">
      ${results.sub_areas.map(area => `<span class="sub-area-tag">${area}</span>`).join('')}
    </div>
  ` : ''}

  ${results?.strategic_insights && results.strategic_insights.length > 0 ? `
    <h2>Strategic Insights</h2>
    <div class="insights-section">
      <ul>
        ${results.strategic_insights.map(insight => `<li>💡 ${insight}</li>`).join('')}
      </ul>
    </div>
  ` : ''}

  <div class="two-column">
    <div class="opportunity-box">
      <h3>✓ Opportunities</h3>
      <ul>
        ${(results?.opportunities || []).map(opp => `<li>${opp}</li>`).join('') || '<li>No opportunities identified</li>'}
      </ul>
    </div>
    <div class="risk-box">
      <h3>⚠ Considerations</h3>
      <ul>
        ${(results?.risks || []).map(risk => `<li>${risk}</li>`).join('') || '<li>No specific risks identified</li>'}
      </ul>
    </div>
  </div>

  <div class="footer">
    <p>Generated by PatTrak - ${new Date().toLocaleDateString()}</p>
    <p>This analysis is based on USPTO patent data and AI interpretation. Verify findings with professional research.</p>
  </div>
</body>
</html>`;

    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `PatTrak_Landscape_${(formData?.technology || 'report').substring(0, 20).replace(/[^a-z0-9]/gi, '_')}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Download as PDF
  const downloadPDF = async () => {
    if (!results) return;
    const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <title>Technology Landscape: ${formData?.technology || 'Report'}</title>
  <style>
    body { font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 40px; line-height: 1.6; }
    h1 { color: #1e293b; font-size: 24px; }
    h2 { color: #7c3aed; font-size: 18px; border-bottom: 2px solid #7c3aed; padding-bottom: 8px; margin-top: 30px; }
    .section { background: #f8fafc; padding: 20px; border-radius: 8px; margin: 15px 0; }
    .maturity-box { background: #dbeafe; padding: 15px; border-radius: 8px; margin: 15px 0; border-left: 4px solid #3b82f6; }
    .stats { display: flex; gap: 20px; margin: 15px 0; }
    .stat { background: #f3e8ff; padding: 15px; border-radius: 8px; text-align: center; flex: 1; }
    .stat-num { font-size: 24px; font-weight: bold; color: #7c3aed; }
    .stat-label { font-size: 12px; color: #64748b; }
    ul { margin: 10px 0; padding-left: 20px; }
    li { margin: 8px 0; }
    .player { background: white; border: 1px solid #e2e8f0; padding: 10px; margin: 8px 0; border-radius: 6px; }
    .sub-area { display: inline-block; background: #f3e8ff; color: #7c3aed; padding: 4px 10px; border-radius: 12px; margin: 3px; font-size: 12px; }
    .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e2e8f0; color: #64748b; font-size: 12px; }
  </style>
</head>
<body>
  <h1>Technology Landscape Report</h1>
  <p><strong>Technology:</strong> ${formData?.technology || 'Not specified'}</p>
  ${formData?.aspect ? '<p><strong>Focus:</strong> ' + formData.aspect + '</p>' : ''}
  <p><strong>Generated:</strong> ${new Date().toLocaleDateString()}</p>

  <div class="maturity-box">
    <strong>Maturity: ${results?.maturity_stage || 'Unknown'}</strong>
    <p>${results?.maturity_explanation || ''}</p>
  </div>

  <div class="stats">
    <div class="stat"><div class="stat-num">${results?.total_patents?.toLocaleString() || 0}</div><div class="stat-label">Patents</div></div>
    <div class="stat"><div class="stat-num">${results?.unique_assignees || 0}</div><div class="stat-label">Companies</div></div>
    <div class="stat"><div class="stat-num">${results?.trend_direction === 'increasing' ? '↑' : results?.trend_direction === 'decreasing' ? '↓' : '→'}</div><div class="stat-label">Trend</div></div>
  </div>

  <h2>Executive Summary</h2>
  <div class="section">${results?.summary || ''}</div>

  ${results?.top_players && results.top_players.length > 0 ? '<h2>Top Players</h2><div class="section">' + results.top_players.map(p => '<div class="player"><strong>' + p.name + '</strong> - ' + p.patents + ' patents<br/><small>' + (p.focus || '') + '</small></div>').join('') + '</div>' : ''}

  ${results?.sub_areas && results.sub_areas.length > 0 ? '<h2>Key Technology Sub-Areas</h2><div class="section">' + results.sub_areas.map(a => '<span class="sub-area">' + a + '</span>').join('') + '</div>' : ''}

  <h2>Strategic Insights</h2>
  <div class="section"><ul>${(results?.strategic_insights || []).map(item => '<li>' + item + '</li>').join('') || '<li>No insights</li>'}</ul></div>

  <h2>Opportunities</h2>
  <div class="section"><ul>${(results?.opportunities || []).map(item => '<li>' + item + '</li>').join('') || '<li>None identified</li>'}</ul></div>

  <h2>Considerations</h2>
  <div class="section"><ul>${(results?.risks || []).map(item => '<li>' + item + '</li>').join('') || '<li>None identified</li>'}</ul></div>

  <div class="footer">Generated by PatTrak - Patent Intelligence Made Simple</div>
</body>
</html>`;
    try {
      const response = await fetch(`${config.API_URL}/api/generate-invention-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          report_html: htmlContent,
          title: formData?.technology || 'Tech_Landscape'
        })
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `PatTrak_Landscape_${(formData?.technology || 'report').substring(0, 20).replace(/[^a-z0-9]/gi, '_')}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        alert('Failed to generate PDF');
      }
    } catch (error) {
      console.error('PDF error:', error);
      alert('Failed to generate PDF');
    }
  };

  // Tier gate - only Premium and Pro users can access
  if (!user || user.tier === 'free') {
    return (
      <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
        <header style={{ padding: '16px', display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#0369a1', fontSize: '14px', fontWeight: '500' }}>
            ← Back
          </button>
        </header>
        
        <main style={{ maxWidth: '600px', margin: '80px auto', padding: '0 16px', textAlign: 'center' }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '48px 32px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
            <div style={{ fontSize: '64px', marginBottom: '24px' }}>🔒</div>
            <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#1e293b', marginBottom: '12px' }}>
              Technology Landscape
            </h1>
            <p style={{ color: '#64748b', fontSize: '16px', lineHeight: '1.6', marginBottom: '32px' }}>
              Explore the patent landscape and identify key players in your technology area.
            </p>
            
            <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '12px', padding: '20px', marginBottom: '32px' }}>
              <p style={{ color: '#0369a1', fontSize: '14px', margin: 0 }}>
                {!user 
                  ? 'Please register or log in to access this tool.'
                  : 'This tool requires a Premium or Pro subscription.'
                }
              </p>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '280px', margin: '0 auto' }}>
              {!user ? (
                <>
                  <button onClick={() => setPage('register')} style={{ padding: '14px 28px', background: '#0369a1', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '16px' }}>
                    Register Free
                  </button>
                  <button onClick={() => setPage('login')} style={{ padding: '14px 28px', background: 'white', color: '#0369a1', border: '1px solid #0369a1', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '16px' }}>
                    Log In
                  </button>
                </>
              ) : (
                <button onClick={() => setPage('pricing')} style={{ padding: '14px 28px', background: '#0369a1', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '16px' }}>
                  Upgrade to Premium
                </button>
              )}
              <button onClick={onBack} style={{ padding: '10px', background: 'none', color: '#64748b', border: 'none', cursor: 'pointer', fontSize: '14px' }}>
                Maybe Later
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <button
          onClick={onBack}
          style={{
            background: 'none',
            border: 'none',
            color: '#0369a1',
            cursor: 'pointer',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: 0,
            marginBottom: '16px'
          }}
        >
          ← Back to Search
        </button>
        
        <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#1e293b', margin: 0 }}>
          🗺️ Technology Landscape
        </h1>
        <p style={{ color: '#64748b', marginTop: '8px', fontSize: '16px' }}>
          Understand who's active in any technology space
        </p>
      </div>

      {/* Progress Steps */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        marginBottom: '32px',
        padding: '16px',
        background: '#f8fafc',
        borderRadius: '8px'
      }}>
        {[
          { num: 1, label: 'Choose Technology' },
          { num: 2, label: 'Analyzing' },
          { num: 3, label: 'Landscape Report' }
        ].map((s, idx) => (
          <React.Fragment key={s.num}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              opacity: step >= s.num ? 1 : 0.4
            }}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: step > s.num ? '#16a34a' : step === s.num ? '#0369a1' : '#e2e8f0',
                color: step >= s.num ? 'white' : '#64748b',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '600',
                fontSize: '14px'
              }}>
                {step > s.num ? '✓' : s.num}
              </div>
              <span style={{ 
                fontSize: '14px', 
                fontWeight: step === s.num ? '600' : '400',
                color: step === s.num ? '#0369a1' : '#64748b'
              }}>
                {s.label}
              </span>
            </div>
            {idx < 2 && (
              <div style={{ 
                flex: 1, 
                height: '2px', 
                background: step > s.num ? '#16a34a' : '#e2e8f0',
                margin: '0 16px'
              }} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: Enter Technology */}
      {step === 1 && (
        <div style={{
          background: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          padding: '32px'
        }}>
          <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#1e293b', marginBottom: '8px' }}>
            What technology area interests you?
          </h2>
          <p style={{ color: '#64748b', marginBottom: '24px' }}>
            I'll map the patent landscape and show you who's active, trends, and key insights.
          </p>

          {/* Technology Area */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>
              Technology / Topic <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <input
              type="text"
              value={formData.technology}
              onChange={(e) => handleInputChange('technology', e.target.value)}
              placeholder="e.g., electric vehicle batteries, CRISPR gene editing, drone delivery"
              style={{
                width: '100%',
                padding: '14px 16px',
                fontSize: '16px',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                outline: 'none'
              }}
              onFocus={(e) => e.target.style.borderColor = '#0369a1'}
              onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
            />
          </div>

          {/* Specific Aspect (optional) */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>
              Specific Aspect <span style={{ color: '#94a3b8' }}>(optional)</span>
            </label>
            <input
              type="text"
              value={formData.aspect}
              onChange={(e) => handleInputChange('aspect', e.target.value)}
              placeholder="e.g., solid-state electrolytes, delivery mechanisms, navigation systems"
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: '15px',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                outline: 'none'
              }}
              onFocus={(e) => e.target.style.borderColor = '#0369a1'}
              onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
            />
          </div>

          {error && (
            <div style={{
              padding: '12px 16px',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              color: '#dc2626',
              marginBottom: '24px',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}

          <button
            onClick={handleSubmit}
            disabled={!formData.technology.trim()}
            style={{
              width: '100%',
              padding: '14px 24px',
              fontSize: '16px',
              fontWeight: '600',
              color: 'white',
              background: !formData.technology.trim() ? '#94a3b8' : '#0369a1',
              border: 'none',
              borderRadius: '8px',
              cursor: !formData.technology.trim() ? 'not-allowed' : 'pointer'
            }}
          >
            Map This Landscape →
          </button>

          {/* Example topics */}
          <div style={{
            marginTop: '24px',
            padding: '16px',
            background: '#f8fafc',
            borderRadius: '8px'
          }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
              💡 Try these examples:
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {['mRNA vaccines', 'autonomous vehicles', 'quantum computing', 'carbon capture', 'blockchain'].map((topic) => (
                <button
                  key={topic}
                  onClick={() => handleInputChange('technology', topic)}
                  style={{
                    padding: '6px 12px',
                    background: '#e0f2fe',
                    border: '1px solid #7dd3fc',
                    borderRadius: '16px',
                    fontSize: '13px',
                    color: '#0369a1',
                    cursor: 'pointer'
                  }}
                >
                  {topic}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Analyzing */}
      {step === 2 && (
        <div style={{
          background: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          padding: '48px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🗺️</div>
          <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#1e293b', marginBottom: '8px' }}>
            Mapping Patent Landscape...
          </h2>
          <p style={{ color: '#64748b', marginBottom: '24px' }}>
            {progress.message}
          </p>
          
          {/* Progress bar */}
          <div style={{
            width: '300px',
            height: '8px',
            background: '#e2e8f0',
            borderRadius: '4px',
            margin: '0 auto 16px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${progress.percent}%`,
              height: '100%',
              background: '#0369a1',
              borderRadius: '4px',
              transition: 'width 0.5s ease'
            }} />
          </div>
          <div style={{ fontSize: '14px', color: '#64748b' }}>
            {progress.percent}% complete
          </div>
        </div>
      )}

      {/* Step 3: Results */}
      {step === 3 && results && (
        <div>
          {/* Maturity Banner */}
          {(() => {
            const maturity = getMaturityColor(results.maturity_stage);
            return (
              <div style={{
                background: maturity.bg,
                border: `1px solid ${maturity.text}40`,
                borderRadius: '12px',
                padding: '24px',
                marginBottom: '24px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '40px', marginBottom: '8px' }}>{maturity.icon}</div>
                <h2 style={{ fontSize: '24px', fontWeight: '700', color: maturity.text, margin: 0 }}>
                  {results.maturity_stage} Technology
                </h2>
                <p style={{ color: maturity.text, marginTop: '8px', fontSize: '15px', opacity: 0.9 }}>
                  {results.maturity_explanation}
                </p>
              </div>
            );
          })()}

          {/* Key Stats */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '16px',
            marginBottom: '24px'
          }}>
            <div style={{
              background: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              padding: '20px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '32px', fontWeight: '700', color: '#0369a1' }}>
                {results.total_patents?.toLocaleString() || '0'}
              </div>
              <div style={{ fontSize: '14px', color: '#64748b' }}>Patents Found</div>
            </div>
            <div style={{
              background: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              padding: '20px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '32px', fontWeight: '700', color: '#0369a1' }}>
                {results.unique_assignees || '0'}
              </div>
              <div style={{ fontSize: '14px', color: '#64748b' }}>Active Companies</div>
            </div>
            <div style={{
              background: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              padding: '20px',
              textAlign: 'center'
            }}>
              <div style={{ 
                fontSize: '32px', 
                fontWeight: '700', 
                color: results.trend_direction === 'increasing' ? '#16a34a' : 
                       results.trend_direction === 'decreasing' ? '#dc2626' : '#64748b'
              }}>
                {results.trend_direction === 'increasing' ? '↑' : 
                 results.trend_direction === 'decreasing' ? '↓' : '→'}
                {results.trend_percentage ? ` ${Math.abs(results.trend_percentage)}%` : ''}
              </div>
              <div style={{ fontSize: '14px', color: '#64748b' }}>Filing Trend</div>
            </div>
          </div>

          {/* Executive Summary */}
          <div style={{
            background: '#f0f9ff',
            border: '1px solid #bae6fd',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#0369a1', marginBottom: '12px' }}>
              📋 Executive Summary
            </h3>
            <p style={{ fontSize: '15px', color: '#0c4a6e', lineHeight: '1.6', margin: 0 }}>
              {results.summary}
            </p>
          </div>

          {/* Top Players */}
          <div style={{
            background: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>
              🏢 Top Players
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {results.top_players?.map((player, idx) => (
                <div key={idx} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    background: idx < 3 ? '#0369a1' : '#e2e8f0',
                    color: idx < 3 ? 'white' : '#64748b',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    {idx + 1}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '500', color: '#1e293b', fontSize: '14px' }}>
                      {player.name}
                    </div>
                    {player.focus && (
                      <div style={{ fontSize: '12px', color: '#64748b' }}>
                        Focus: {player.focus}
                      </div>
                    )}
                  </div>
                  <div style={{ 
                    fontSize: '14px', 
                    fontWeight: '600', 
                    color: '#0369a1',
                    background: '#e0f2fe',
                    padding: '4px 12px',
                    borderRadius: '12px'
                  }}>
                    {player.patents} patents
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Technology Sub-Areas */}
          {results.sub_areas && results.sub_areas.length > 0 && (
            <div style={{
              background: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              padding: '24px',
              marginBottom: '24px'
            }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>
                🔬 Key Technology Sub-Areas
              </h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {results.sub_areas.map((area, idx) => (
                  <span key={idx} style={{
                    padding: '8px 16px',
                    background: '#f1f5f9',
                    borderRadius: '20px',
                    fontSize: '13px',
                    color: '#475569'
                  }}>
                    {area}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Strategic Insights */}
          <div style={{
            background: '#fef3c7',
            border: '1px solid #fcd34d',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#92400e', marginBottom: '12px' }}>
              💡 Strategic Insights
            </h3>
            <ul style={{ margin: 0, paddingLeft: '20px', color: '#78350f', fontSize: '14px', lineHeight: '1.8' }}>
              {results.strategic_insights?.map((insight, idx) => (
                <li key={idx}>{insight}</li>
              ))}
            </ul>
          </div>

          {/* Opportunities & Risks */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '24px',
            marginBottom: '24px'
          }}>
            <div style={{
              background: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: '12px',
              padding: '24px'
            }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#166534', marginBottom: '12px' }}>
                ✓ Opportunities
              </h3>
              <ul style={{ margin: 0, paddingLeft: '20px', color: '#15803d', fontSize: '14px', lineHeight: '1.8' }}>
                {results.opportunities?.map((opp, idx) => (
                  <li key={idx}>{opp}</li>
                ))}
              </ul>
            </div>

            <div style={{
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '12px',
              padding: '24px'
            }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#dc2626', marginBottom: '12px' }}>
                ⚠️ Considerations
              </h3>
              <ul style={{ margin: 0, paddingLeft: '20px', color: '#b91c1c', fontSize: '14px', lineHeight: '1.8' }}>
                {results.risks?.map((risk, idx) => (
                  <li key={idx}>{risk}</li>
                ))}
              </ul>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button
              onClick={resetJourney}
              style={{
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#0369a1',
                background: 'white',
                border: '2px solid #0369a1',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              Explore Another Technology
            </button>
            <button
              onClick={saveReport}
              disabled={saving}
              style={{
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: '500',
                color: 'white',
                background: saveSuccess ? '#16a34a' : '#0369a1',
                border: 'none',
                borderRadius: '8px',
                cursor: saving ? 'not-allowed' : 'pointer'
              }}
            >
              {saving ? 'Saving...' : saveSuccess ? 'Saved!' : 'Save Report'}
            </button>
            <button
              onClick={downloadHTML}
              style={{
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                background: 'white',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              Download HTML
            </button>
            <button
              onClick={downloadPDF}
              style={{
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: '500',
                color: 'white',
                background: '#0369a1',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              Download PDF
            </button>
          </div>

          {/* What You Learned */}
          <div style={{
            marginTop: '32px',
            padding: '16px',
            background: '#f8fafc',
            borderRadius: '8px',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
              📚 What you learned
            </div>
            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', color: '#64748b' }}>
              <li>How to assess technology maturity through patent data</li>
              <li>Identifying key players and competitive landscape</li>
              <li>Spotting opportunities in emerging technology areas</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default TechLandscapePage;